import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "src/dm/utils/axios";
import { APIS } from "src/dm/utils/constants";

/**
 * Response shape for POST /deals/{dealId}/items/add-by-family.
 * Mirrors `AddByFamilyResponse` on the API side. `incompleteFamilies`
 * surfaces the legacy `incompleteFamilies` popup data — empty list means
 * the SPA can skip the follow-up modal.
 */
export interface AddByFamilyResult {
  addedDealItemIds: number[];
  skippedRetailItemNumbers: number[];
  incompleteFamilies: number[];
}

interface AddByFamilyBody {
  familyCode: number;
  /**
   * Set to `true` to include every catalog row in the named family
   * (legacy `addWholeFamily` toggle). Default `true` because that is the
   * only branch the legacy UI ever calls — the API still accepts `false`
   * for forward compatibility.
   */
  includeAllSiblings?: boolean;
}

/**
 * Add every item in a family to the deal in one round-trip. Mirrors the
 * legacy `SearchItem.aspx` / `AddRewardItem.aspx` `addWholeFamily`
 * branch. The server runs `MarginCalculationPkg` post-insert so the SPA
 * just needs to invalidate the items cache on success.
 */
export const useAddByFamily = (dealId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: AddByFamilyBody): Promise<AddByFamilyResult> => {
      const url = `${APIS.Deals}/${dealId}/items/add-by-family`;
      const response = await apiClient.post<AddByFamilyResult>(url, {
        familyCode: body.familyCode,
        includeAllSiblings: body.includeAllSiblings ?? true,
      });
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deal-items", dealId] });
    },
  });
};
